// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

/**
 * COB-263 — host-side helpers for the verifier-served confirm embed.
 *
 * Two concerns live here so the modal component stays thin and the
 * security-critical bits are unit-testable in isolation:
 *
 *   1. `verifierOriginForNetwork` — the single verifier origin we frame
 *      (and origin-pin) per bitcoin network. Hard-coded on purpose, in
 *      lockstep with `verifierVaultUrlForNetwork` (vault-detail) and the
 *      `VERIFIER_ORIGINS_BY_NETWORK` allow-list (sign-verifier-challenge):
 *      a typo'd env var must never widen the trust boundary. A test
 *      cross-checks every origin against `isAllowedReturnOrigin`.
 *
 *   2. `produceOwnershipSignatureReply` — the parent sign handler as a
 *      pure function. The embed asks us to sign an ownership challenge;
 *      we ONLY ever sign for the vault the modal is confirming, only a
 *      well-formed nonce, and we reconstruct the canonical message inside
 *      the key-agent (we pass it `(vaultId, nonceHex)`, never a caller-
 *      supplied body). COB-78 (hardware) slots in at exactly one point —
 *      the `OwnershipSigner.signOwnershipChallenge` implementation — with
 *      no change to this function or the postMessage protocol.
 */

import { isAllowedReturnOrigin } from './pages/sign-verifier-challenge.js';
import {
  signatureErrorMessage,
  signatureMessage,
  type EmbedParentMessage,
} from './embed-protocol.js';

/** 64 lowercase hex chars — the exact verifier ownership-nonce shape (see
 *  `NONCE_RE` in sign-verifier-challenge). Kept local so this module has
 *  no dependency on the popup page's internals. */
const NONCE_RE = /^[0-9a-f]{64}$/;

/**
 * The verifier origin to embed for a given bitcoin network, or `null` for
 * an unknown/absent network (the caller then suppresses the embed rather
 * than framing a wrong host). Mirrors `verifierVaultUrlForNetwork`'s host
 * choice exactly — signet picks the M-series staging verifier; testnet +
 * bitcoin pick the legacy/mainnet host.
 */
export function verifierOriginForNetwork(network: string | null): string | null {
  switch (network) {
    case 'regtest':
      return 'http://localhost:5174';
    case 'signet':
      return 'https://verifier.staging.cobault.io';
    case 'testnet':
    case 'bitcoin':
      return 'https://app.verifier.cobault.io';
    default:
      return null;
  }
}

/**
 * `true` iff `origin` is a verifier origin we are permitted to frame on
 * `network`. Reuses the sign-verifier-challenge allow-list so the embed
 * and the popup share one source of truth for "which verifier origins are
 * trusted per network".
 */
export function isEmbeddableVerifierOrigin(
  origin: string,
  network: string | null,
): boolean {
  if (network === null) return false;
  return isAllowedReturnOrigin(origin, network);
}

/** The embed URL for a vault at a given (already-validated) verifier
 *  origin. The vault id is URL-encoded so a malformed id can never alter
 *  the path structure. */
export function embedConfirmUrl(verifierOrigin: string, vaultId: string): string {
  return `${verifierOrigin}/embed/confirm/${encodeURIComponent(vaultId)}`;
}

// ── parent sign handler ─────────────────────────────────────────────

/** The single key-agent capability the embed sign path needs. Narrower
 *  than the full `KeyAgentClient` on purpose: it is the whole surface
 *  COB-78's hardware path has to satisfy, and it keeps this module's unit
 *  tests free of the Worker-backed client. */
export interface OwnershipSigner {
  isUnlocked(): Promise<boolean>;
  signOwnershipChallenge(
    vaultId: string,
    nonceHex: string,
    derivationIndexHint?: number,
  ): Promise<string>;
}

export interface ProduceSignatureArgs {
  /** The vault the modal is confirming — the ONLY vault we will sign for.
   *  Comes from the modal's own `/embed/confirm/{id}` URL, never the
   *  message. */
  expectedVaultId: string;
  /** The `vault_id` the embed asked us to sign (UNTRUSTED — from the
   *  postMessage). Must equal `expectedVaultId` or we refuse. */
  requestedVaultId: string;
  /** The nonce the embed asked us to sign (UNTRUSTED). Must be 64 hex. */
  nonceHex: string;
  /** The in-context key agent, or `null` when there is no session. */
  agent: OwnershipSigner | null;
  /** Best-effort derivation-index hint to heal a missing local mapping
   *  (see sign-verifier-challenge). Optional. */
  derivationIndexHint?: number;
}

/**
 * Produce the reply to an embed `request-signature`, as a pure function of
 * its inputs. Never throws — every failure path returns a
 * `signature-error` envelope with a short reason code.
 *
 * Security invariants (each has a test):
 *   - Refuses to sign for any vault other than `expectedVaultId`
 *     (`foreign-vault`). The embed is served for one vault; a request
 *     naming a different one is hostile.
 *   - Refuses a malformed nonce (`bad-nonce`). We only ever sign a
 *     well-formed challenge, and we reconstruct the canonical message
 *     inside the signer — the caller-supplied fields are never treated as
 *     a message body.
 *   - Fails closed with no session / a locked agent (`no-session` /
 *     `locked`).
 *
 * The reply is always keyed to `expectedVaultId` + `nonceHex` so the honest
 * embed's `isEmbedSignatureFor(vault, nonce)` guard can correlate it.
 */
export async function produceOwnershipSignatureReply(
  args: ProduceSignatureArgs,
): Promise<EmbedParentMessage> {
  const { expectedVaultId, requestedVaultId, nonceHex, agent } = args;

  // Never sign for a vault other than the one this modal is confirming.
  if (requestedVaultId !== expectedVaultId) {
    return signatureErrorMessage(expectedVaultId, nonceHex, 'foreign-vault');
  }
  // Only ever sign a well-formed challenge nonce — never a caller body.
  if (!NONCE_RE.test(nonceHex)) {
    return signatureErrorMessage(expectedVaultId, nonceHex, 'bad-nonce');
  }
  if (!agent) {
    return signatureErrorMessage(expectedVaultId, nonceHex, 'no-session');
  }

  try {
    if (!(await agent.isUnlocked())) {
      return signatureErrorMessage(expectedVaultId, nonceHex, 'locked');
    }
    // Pass ONLY (vaultId, nonce) to the signer; it reconstructs
    // `cobault-verifier-ownership-v1:{vault}:{nonce}` itself. `vaultId` is
    // the trusted `expectedVaultId`, never the message's field.
    const signatureDerHex = await agent.signOwnershipChallenge(
      expectedVaultId,
      nonceHex,
      args.derivationIndexHint,
    );
    return signatureMessage(expectedVaultId, nonceHex, signatureDerHex);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    const reason = message.includes('No derivation index found')
      ? 'no-key'
      : 'sign-failed';
    return signatureErrorMessage(expectedVaultId, nonceHex, reason);
  }
}
